import { botLink } from './deepLink';

/**
 * Sign-in in an ordinary browser (#4218, docs/superpowers/specs/2026-10-29-web-login-design.md). Only the mini app
 * can sign in (signed initData), so the browser opens it with `startapp=web_<state>`, and the mini app opens the
 * browser back with a token of its own in the URL fragment. The fragment never reaches a server; the state ties
 * the answer to the browser that asked.
 */

const STATE_PATTERN = /^web_([A-Za-z0-9_-]{22,43})$/;

/** 128 random bits, base64url: fits Telegram's startapp limit (64 chars of A-Za-z0-9_-). */
export function newWebState(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function webLoginLink(state: string, base: string = import.meta.env.BASE_URL): string {
  return `${botLink(base)}?startapp=web_${state}`;
}

export function webLoginStateFromInitData(initData: string): string | null {
  return STATE_PATTERN.exec(new URLSearchParams(initData).get('start_param') ?? '')?.[1] ?? null;
}

export function handoffUrl(origin: string, base: string, token: string, state: string): string {
  return `${origin}${base}#webToken=${encodeURIComponent(token)}&state=${encodeURIComponent(state)}`;
}

export function readHandoff(hash: string): { token: string; state: string | null } | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('webToken');
  return token ? { token, state: params.get('state') } : null;
}

/**
 * The app's address is app.pass24online.ru (build variable VITE_WEB_ORIGIN) for Telegram, Max and the browser; the old
 * tg.pass24online.ru serves the same build and sends browsers here. Local and mock builds leave it empty: same origin.
 */
export function webOrigin(current: string, configured: string | undefined): string {
  return configured || current;
}

/** A browser on any other address of the app goes to the web address; a token is per address (localStorage). */
export function webAddressRedirect(origin: string, pathname: string, search: string, hash: string, configured: string | undefined): string | null {
  if (!configured || origin === configured) return null;
  return `${configured}${pathname}${search}${hash}`;
}
